/**
 * Qui est desservi à cette adresse — la seule question que pose la racine.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * LE DÉFAUT RÉPARÉ
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `/` servait la vitrine du PRODUIT à tout le monde. Sur
 * `<slug>.ticket.ilygo.ch`, un spectateur venu chercher les dates de son
 * théâtre tombait donc sur une page de vente de logiciel — et le théâtre
 * hébergeait la publicité de son fournisseur sur sa propre adresse
 * (SPEC-BOUTIQUE §8 bis D bis).
 *
 * ────────────────────────────────────────────────────────────────────────────
 * POURQUOI LA DÉCISION VIENT DU BACKEND, ET PAS DU NOM D'HÔTE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Le réflexe serait d'analyser `location.hostname` comme le fait
 * `features/console/hote.ts` : « une étiquette de plus que le domaine, donc un
 * théâtre ». Ce réflexe est faux ici, et le prouver coûte une ligne :
 * `localhost:5173` est l'apex en développement multi-tenant, mais c'est
 * **l'adresse du théâtre** quand l'instance tourne en mono-tenant
 * (`MULTITENANT=false` — c'est exactement la configuration des parcours
 * Playwright, `frontend/playwright.config.ts`). Le même nom d'hôte désigne
 * donc deux choses opposées selon une variable d'environnement du BACKEND.
 *
 * Un déploiement mono-tenant sur le domaine propre d'un théâtre
 * (`billetterie.exemple.ch`) pose la même colle : aucune analyse de chaîne
 * faite dans le navigateur ne peut y répondre.
 *
 * La seule couche qui sache est celle qui résout le tenant. Et elle le dit
 * déjà, sans qu'on ajoute quoi que ce soit : `GET /theatre/publique` est de
 * portée `tenant` (`backend/src/plateforme/hooks.ts`), donc elle répond
 * **404 `TENANT_INCONNU`** là où aucun théâtre n'est desservi — l'apex — et
 * 200 partout ailleurs. Le front et le backend décident la même chose à partir
 * du même signal, comme pour la console.
 *
 * ★ **Fermé par défaut, dans le sens qui protège le client.** Toute autre
 *   réponse — 200, panne réseau, théâtre suspendu, essai échu — vaut « un
 *   théâtre est desservi ici ». La vitrine commerciale n'apparaît donc JAMAIS
 *   par accident sur l'adresse d'un théâtre : il faut que le serveur ait dit,
 *   explicitement, qu'il n'y a personne à cette adresse.
 */

import { ApiError } from '../../lib/api'

/**
 * `true` si le serveur a dit qu'aucun théâtre n'est desservi à cette adresse —
 * c'est-à-dire l'apex du domaine, la seule place de la vitrine du produit.
 *
 * @param erreur l'erreur rendue par `GET /theatre/publique` (`undefined` ou
 *   `null` quand la requête a abouti).
 */
export function adresseSansTheatre(erreur: unknown): boolean {
  return erreur instanceof ApiError && erreur.code === 'TENANT_INCONNU'
}
